import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useLang } from "../i18n";
import { Reveal } from "../components/Reveal";
import { Youtube, ExternalLink, RefreshCw, Database } from "lucide-react";

export default function YouTubePage() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const locale = lang === "it" ? "it-IT" : "en-GB";

  useEffect(() => {
    let cancelled = false;
    let timer;
    const load = (isRetry = false) => {
      api.get("/youtube/videos").then(({ data }) => {
        if (cancelled) return;
        setData(data);
        if (data.videos.length) {
          setSelected(data.videos[0]);
        } else if (!isRetry) {
          // archivio vuoto: quasi sempre un errore temporaneo del feed — un solo retry automatico dopo 2.5s
          timer = setTimeout(() => load(true), 2500);
        }
      }).catch(() => {
        if (cancelled) return;
        if (!isRetry) timer = setTimeout(() => load(true), 2500);
        else setData({ videos: [], live: false });
      });
    };
    load();
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  const videos = data?.videos || [];

  return (
    <main className="pt-32 pb-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="hud-label mb-3 text-wardogs">{t("yt.over")}</p>
          <h1 className="font-display text-6xl md:text-8xl uppercase leading-[0.9] mb-4">
            {t("yt.title1")} <span className="text-wardogs">{t("yt.title2")}</span>
          </h1>
          <p className="text-steel text-base md:text-lg max-w-xl mb-4">
            {t("yt.text")}
          </p>
          <div className="flex items-center gap-2 mb-14" data-testid="youtube-feed-status">
            {data === null ? (
              <span className="hud-label flex items-center gap-2"><RefreshCw className="w-3 h-3 animate-spin" /> {t("yt.sync")}</span>
            ) : data.live ? (
              <span className="hud-label flex items-center gap-2"><span className="w-2 h-2 bg-manticore animate-blink" /> {t("yt.live", { n: data.total_seen || videos.length })}</span>
            ) : (
              <span className="hud-label flex items-center gap-2"><Database className="w-3 h-3" /> {t("yt.down")}</span>
            )}
          </div>
        </Reveal>

        {selected && (
          <Reveal data-testid="youtube-featured">
            <div className="chamfer border border-[#1A1A1A] bg-[#0C0C0C] overflow-hidden mb-px relative">
              <div className="absolute top-0 left-0 right-0 h-px bg-wardogs/60 z-10" />
              <div className="grid lg:grid-cols-[1fr_360px]">
                <div className="aspect-video">
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${selected.id}`}
                    title={selected.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    data-testid="youtube-featured-embed"
                  />
                </div>
                <div className="p-6 flex flex-col justify-between gap-6">
                  <div>
                    <p className="hud-label mb-3">{t("yt.latest")}</p>
                    <h2 className="font-display text-3xl uppercase leading-tight">{selected.title}</h2>
                    <p className="mt-3 font-mono text-[11px] text-steel">
                      {selected.published && !isNaN(new Date(selected.published)) && new Date(selected.published).toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" })}
                    </p>
                  </div>
                  <a
                    href={selected.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="youtube-featured-link"
                    className="chamfer-sm inline-flex items-center justify-center gap-2 bg-[#FF0000] hover:bg-[#cc0000] text-white font-mono text-[11px] uppercase tracking-[0.2em] font-bold px-6 py-3.5 transition-colors duration-300"
                  >
                    <Youtube className="w-4 h-4" /> {t("yt.watch")}
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        )}

        <div className="grid gap-px bg-[#1A1A1A] border border-[#1A1A1A] sm:grid-cols-2 lg:grid-cols-3 mt-px">
          {videos.map((v, i) => (
            <Reveal key={v.id} delay={Math.min(i, 5) * 0.08} className="bg-[#050505]">
              <button
                onClick={() => { setSelected(v); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                data-testid={`youtube-video-${v.id}`}
                className="group relative block w-full text-left p-4 overflow-hidden"
              >
                <div className="relative overflow-hidden chamfer-sm border border-[#1A1A1A]">
                  <img src={v.thumbnail} alt={v.title} loading="lazy" className="w-full aspect-video object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-[#050505]/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-wardogs">{t("yt.play")}</span>
                  </div>
                </div>
                <div className="mt-4 flex items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-xl uppercase leading-tight group-hover:text-wardogs transition-colors duration-300">{v.title}</p>
                    <p className="mt-1 font-mono text-[10px] text-steel">
                      {v.published && !isNaN(new Date(v.published)) && new Date(v.published).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" })}
                    </p>
                  </div>
                  <ExternalLink className="w-4 h-4 mt-1 shrink-0 text-[#2A2A2A] group-hover:text-wardogs transition-colors duration-300" />
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-14">
          <a
            href="https://www.youtube.com/@BULKHEAD."
            target="_blank"
            rel="noopener noreferrer"
            data-testid="youtube-channel-link"
            className="chamfer inline-flex items-center gap-3 border border-[#2A2A2A] hover:border-wardogs text-white font-mono text-xs uppercase tracking-[0.25em] px-8 py-4 transition-colors duration-300"
          >
            <Youtube className="w-4 h-4 text-[#FF0000]" /> {t("yt.channel")}
          </a>
        </Reveal>
      </div>
    </main>
  );
}
